import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n';
import {
  Calculator,
  Scale,
  TrendingUp,
  FileBarChart,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';

const ACCOUNTING_PATHS = ['/app/finances', '/app/financial', '/app/accounting/reports'];

const subItems = [
  { to: '/app/finances', icon: Scale, labelKey: 'nav.balance' },
  { to: '/app/financial', icon: TrendingUp, labelKey: 'nav.financial' },
  { to: '/app/accounting/reports', icon: FileBarChart, labelKey: 'nav.reports' },
];

export function AccountingNavGroup({ collapsed }) {
  const { t } = useI18n();
  const location = useLocation();
  const inSection = ACCOUNTING_PATHS.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(inSection);

  useEffect(() => {
    if (inSection) setOpen(true);
  }, [inSection]);

  if (collapsed) {
    return (
      <ul className="space-y-1">
        {subItems.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              title={t(item.labelKey)}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-center px-3 py-2.5 rounded-sm transition-colors',
                  isActive
                    ? 'bg-domio-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-[#0A2F52]',
                )
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
            </NavLink>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors',
          inSection ? 'text-white' : 'text-slate-400 hover:text-white hover:bg-[#0A2F52]',
        )}
        data-testid="nav-accounting-toggle"
      >
        <Calculator className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left">{t('nav.accounting')}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 opacity-70" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-0.5 pl-2">
        {subItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ml-2 border-l-2',
                isActive
                  ? 'border-domio-green bg-domio-600/90 text-white'
                  : 'border-transparent text-slate-400 hover:text-white hover:bg-[#0A2F52]',
              )
            }
            data-testid={`nav-${item.labelKey.split('.').pop()}`}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export default AccountingNavGroup;
